import { Box, Code, Divider, HStack, ListItem, OrderedList, Tag, Text } from "@chakra-ui/react";
import { FC } from "react";

import {
  FeatureCard,
  FeatureGrid,
  PageCta,
  PageHero,
  PageSection,
  SectionHeading,
  usePageTitle,
} from "./components";

const SUPPORTED_FORMATS = [
  "CSV",
  "Excel (.xlsx)",
  "Meetnetbeheer software",
  "Lizard",
  "FEWS",
  "Wiski",
  "REST API",
];

export const Integraties: FC = () => {
  usePageTitle("Integraties");

  return (
    <Box>
      <PageHero
        title="Integraties"
        subtitle="BROSTAR sluit aan op uw bestaande workflows en systemen. Importeer data uit gangbare formaten of koppel via onze REST API."
      />

      <PageSection>
        <SectionHeading>Wat is een koppeling bij BROSTAR?</SectionHeading>
        <Text fontWeight="400" color="gray.700" lineHeight="tall" maxW="800px" mx="auto" mb={4}>
          In de kern komt elke koppeling met BROSTAR op hetzelfde neer: uw bronsysteem stuurt
          gegevens als JSON-bericht naar de BROSTAR-API, die het bericht valideert en doorzet naar
          de BRO. Dit is dezelfde generieke aanpak voor ieder bronsysteem, of het nu gaat om een
          meetnetbeheerpakket, een SCADA-systeem, GIS of een data warehouse.
        </Text>
        <Text fontWeight="400" color="gray.700" lineHeight="tall" maxW="800px" mx="auto">
          Omdat de kern van de koppeling steeds hetzelfde is, zit het werk vooral in het eenmalig
          afstemmen van de mapping tussen uw bronsysteem en het BRO-model, niet in het bouwen van
          nieuwe software.
        </Text>
      </PageSection>

      <PageSection>
        <SectionHeading>Ondersteunde invoerformaten</SectionHeading>
        <HStack spacing={3} justify="center" wrap="wrap" mb={16}>
          {SUPPORTED_FORMATS.map((fmt) => (
            <Tag key={fmt} size="lg" bg="accentLight" color="text" fontWeight="700" px={5} py={2}>
              {fmt}
            </Tag>
          ))}
        </HStack>

        <FeatureGrid>
          <FeatureCard title="CSV-import">
            Importeer grondwaterstanden, analyseresultaten en meetpuntgegevens via
            gestandaardiseerde CSV-sjablonen. Download de sjablonen direct vanuit de applicatie.
          </FeatureCard>
          <FeatureCard title="Excel-import">
            Upload uw meetgegevens als Excel-bestand. BROSTAR verwerkt de data, valideert de inhoud
            en zet alles om naar geldige BRO-berichten.
          </FeatureCard>
          <FeatureCard title="Koppeling met meetnetbeheer software">
            Wij kunnen u helpen met het opzetten van een koppeling tussen BROSTAR en uw
            meetnetbeheer software, zoals de BRO-Connector, of een andere beheeromgeving die het
            exporteren van gegevens mogelijk maakt.
          </FeatureCard>
          <FeatureCard title="Tijdseries uitwisselen met Lizard, FEWS of Wiski">
            BROSTAR kan worden gekoppeld aan veel gebruikte opslag methodes voor grondwaterstanden,
            zoals Lizard, FEWS of Wiski. Doordat we al een groot gedeelte van deze workflow voor
            anderen hebben ontwikkeld, valt de hoeveelheid maatwerk en configuratie voor u mee.
          </FeatureCard>
          <FeatureCard title="REST API">
            Koppel uw eigen systemen via de BROSTAR REST API. Stuur BRO-registraties programmatisch
            in vanuit uw databeheerapplicatie, SCADA-systeem of data-pipeline.
          </FeatureCard>
        </FeatureGrid>
      </PageSection>

      <PageSection>
        <Divider mb={16} />
        <SectionHeading>Ervaring met uw bronsystemen en techstack</SectionHeading>
        <Text
          fontWeight="400"
          color="gray.700"
          lineHeight="tall"
          maxW="800px"
          mx="auto"
          mb={10}
          textAlign="center"
        >
          U hoeft geen werkend systeem om te gooien om aan de BRO-verplichting te voldoen. BROSTAR
          sluit aan op de systemen die u al gebruikt, zodat u kunt vasthouden aan het werkproces dat
          het beste bij uw organisatie past.
        </Text>
        <FeatureGrid>
          <FeatureCard title="Dataloggers en meetnetbeheer">
            We hebben ervaring met het koppelen van dataloggers en meetnetbeheersystemen zoals
            Munisense, Koenders en Van Essen.
          </FeatureCard>
          <FeatureCard title="Tijdreeksen en ruimtelijke data">
            Voor tijdreeksen sluiten we aan op FEWS. Ruimtelijke gegevens, zoals de locatie en
            eigenschappen van putten, koppelen we vanuit uw GIS-omgeving.
          </FeatureCard>
          <FeatureCard title="Maatwerk Django-applicaties">
            Ook complete koppelapplicaties hebben we gerealiseerd en in beheer, zoals de
            BRO-Connector (voor provincie Zeeland) en Objectenbeheer (voor Vitens).
          </FeatureCard>
          <FeatureCard title="Validatiepartners in de keten">
            We werken nauw samen met Artesia: hun tools FieldForm, ArtDiver en DataLens zetten we in
            voor grondwatervalidatie binnen de aanleverketen.
          </FeatureCard>
        </FeatureGrid>
      </PageSection>

      <PageSection>
        <Divider mb={16} />
        <SectionHeading>Hoe een koppeling tot stand komt</SectionHeading>
        <OrderedList
          spacing={4}
          maxW="800px"
          mx="auto"
          color="gray.700"
          fontWeight="400"
          lineHeight="tall"
          mb={10}
        >
          <ListItem>
            <b>Intake</b> — we bespreken welk bronsysteem u gebruikt en welke gegevens u wilt
            aanleveren.
          </ListItem>
          <ListItem>
            <b>Aanlevermethode bepalen</b> — een koppeling die reageert op een trigger (bijvoorbeeld
            een nieuwe meting) of een periodieke taak (bijvoorbeeld dagelijks of wekelijks).
          </ListItem>
          <ListItem>
            <b>JSON-mapping opzetten</b> — de gegevens uit uw bronsysteem worden vertaald naar het
            BRO-model.
          </ListItem>
          <ListItem>
            <b>Testfase</b> — we testen de koppeling op testomgevingen van de BRO, zodat fouten
            worden opgespoord voordat er productieberichten worden verstuurd.
          </ListItem>
          <ListItem>
            <b>Livegang en monitoring</b> — na livegang monitoren we de koppeling, zodat u erop kunt
            vertrouwen dat berichten aankomen.
          </ListItem>
        </OrderedList>
        <Text fontWeight="400" color="gray.700" lineHeight="tall" maxW="800px" mx="auto">
          Voor systemen waarmee we al ervaring hebben, zoals FEWS, Wiski, Lizard, veelgebruikte
          meetnet- en objectenbeheersystemen, of GIS, is de mapping vaak al grotendeels bekend. Dat
          maakt het traject korter en voorspelbaarder. Hoe verder het datamodel van uw bronsysteem
          afwijkt van het BRO-model, hoe meer maatwerk de mapping vraagt, en hoe hoger de kosten en
          doorlooptijd navenant uitvallen.
        </Text>
      </PageSection>

      <PageSection>
        <Divider mb={16} />
        <SectionHeading>API-documentatie</SectionHeading>
        <Box bg="gray.900" rounded="large" p={8} maxW="700px" mx="auto" fontFamily="monospace">
          <Text fontSize="xs" color="gray.400" mb={4} fontWeight="400">
            Voorbeeld: tijdreeks aanleveren via de BROSTAR API
          </Text>
          <Code
            display="block"
            whiteSpace="pre"
            bg="transparent"
            color="green.300"
            fontSize="sm"
            overflow="auto"
          >
            {`POST /api/v1/uploadtasks/
Authorization: Token <uw-api-token>
Content-Type: application/json

{
  "bro_domain": "GLD",
  "registration_type": "GLD_Addition",
  "request_type": "registration",
  "project_number": "1234",
  "metadata": { ... },
  "sourcedocument_data": { ... }
}`}
          </Code>
        </Box>
        <Text fontWeight="400" fontSize="sm" color="gray.500" textAlign="center" mt={4}>
          Volledige API-documentatie beschikbaar op{" "}
          <Box
            as="a"
            href="https://docs.brostar.nl"
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            fontWeight="700"
          >
            docs.brostar.nl
          </Box>
        </Text>
      </PageSection>

      <PageCta
        title="Kan uw bronsysteem koppelen met BROSTAR?"
        body="Neem vrijblijvend contact op en we denken met u mee over de mogelijkheden en het traject."
      />
    </Box>
  );
};
